/*
 * SPDX-FileCopyrightText: 2023 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import type { ModalVisibilityProps } from '../../../common/modals/common-modal';
import { CommonModal } from '../../../common/modals/common-modal';
import { RevisionList } from './revision-list';
import { RevisionModalFooter } from './revision-modal-footer';
import styles from './revision-modal.module.scss';
import { RevisionViewer } from './revision-viewer';
import React, { useMemo, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { ClockHistory as IconClockHistory, Journal } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
// import { DiffMethod } from 'react-diff-viewer';
import { diffMethodArray } from './utils';
import { useLazyFetchVersionContentQuery } from '@services/versionHistoryService';
import RevisionModalSubHeader from './revision-modal-sub-header';
import { UserDataType } from '@api/revisions/types';
import { ShowIf } from '@components/common/show-if/show-if';
import DraftViewer from '../drafts/draft-viewer';
import { useApplicationState } from '@hooks/common/use-application-state';

export type DetailType = {
  date: string;
  userData: UserDataType;
};

export type VersionIndexType = {
  oldVerIndex: number | undefined;
  newVerIndex: number | undefined;
  verDetails: {
    old: DetailType;
    new: DetailType;
  };
};

/**
 * Modal that shows the available revisions and allows for comparison between them.
 *
 * @param show true to show the modal, false otherwise.
 * @param onHide Callback that is fired when the modal is requested to close.
 */
export const RevisionModal: React.FC<ModalVisibilityProps & {
  isDraft: boolean;
}> = ({
  show,
  onHide,
  isDraft,
}) => {
  useTranslation();
  const [selectedVersionIndex, setSelectedVersionIndex] =
    useState<VersionIndexType>();
  const [compareMethod, setCompareMethod] = useState<string>(
    diffMethodArray[0]
  );
  const { articleContent, draftContent } = useApplicationState((state) => ({
    articleContent: state.noteDetails.markdownContent.plain,
    draftContent: state.noteDraft.content,
  }));
  const [isSplitView, setIsSplitView] = useState<boolean>(true);
  const [fullScreen, setFullScreen] = useState(); // undefined | true
  const [
    versionContentTrigger,
    {
      data: versionContentData,
      isLoading: versionContentIsLoading,
      error: versionContentError,
      isFetching: versionContentIsFetching
    }
  ] = useLazyFetchVersionContentQuery();
  const [leftSideToggle, setLeftSideToggle] = useState(true);

  const errorToProp = useMemo(() => {
    if (versionContentError) {
      if ('message' in versionContentError) {
        return new Error(versionContentError.message);
      } else if ('status' in versionContentError) {
        return new Error(
          `HTTP error with status code ${versionContentError.status}`
        );
      }
    }
    return undefined;
  }, [versionContentError]);

  return (
    <CommonModal
      show={show}
      onHide={() => {
        onHide?.();
        setSelectedVersionIndex(undefined);
      }}
      fullscreen={fullScreen}
      titleI18nKey={
        isDraft ? 'editor.modal.draft.title' : 'editor.modal.revision.title'
      }
      titleIcon={isDraft ? Journal : IconClockHistory}
      showCloseButton={true}
      modalSize={'xl'}
      additionalClasses={styles['revision-modal']}>
      <RevisionModalSubHeader
        setCompareMethod={setCompareMethod}
        setIsSplitView={setIsSplitView}
        isSplitView={isSplitView}
        compareMethod={compareMethod}
        fullscreen={fullScreen}
        setFullscreen={setFullScreen}
        setLeftSideToggle={setLeftSideToggle}
        selectedVersionIndex={selectedVersionIndex}
        leftSideToggle={leftSideToggle}
        isDraft={isDraft}
        onHide={onHide!}
      />
      <Modal.Body>
        {isDraft ? (
          <div className='overflow-scroll'>
            <DraftViewer
              oldContent={articleContent}
              newContent={draftContent}
              isSplitView={isSplitView}
              compareMethod={compareMethod}
            />
          </div>
        ) : (
          <Row>
            <ShowIf condition={!((!leftSideToggle && selectedVersionIndex !== undefined) || isDraft)}>
              <Col lg={4} className={styles['scroll-col']}>
                <RevisionList
                  onVersionSelect={setSelectedVersionIndex}
                  selectedVersionIndex={selectedVersionIndex}
                  versionContentTrigger={versionContentTrigger}
                />{' '}
              </Col>
            </ShowIf>
            <Col
              lg={!leftSideToggle && selectedVersionIndex !== undefined ? 0 : 8}
              className={cn(
                styles['scroll-col'],
                'd-flex',
                {
                  'flex-column justify-content-between':
                    selectedVersionIndex !== undefined
                },
                {
                  'align-items-center justify-content-center':
                    selectedVersionIndex === undefined
                }
              )}>
              <RevisionViewer
                versionContent={{
                  versionContentData,
                  versionContentIsLoading,
                  versionContentError: errorToProp,
                  versionContentIsFetching
                }}
                isSplitView={isSplitView}
                compareMethod={compareMethod}
                selectedVersionIndex={selectedVersionIndex}
              />
            </Col>
          </Row>
        )}
      </Modal.Body>
      <ShowIf condition={!isDraft}>
        <RevisionModalFooter
          selectedVersionIndex={selectedVersionIndex}
          versionContentTrigger={versionContentTrigger}
          onVersionSelect={setSelectedVersionIndex}
          onHide={onHide}
          setSelectedVersionIndex={setSelectedVersionIndex}
        />
      </ShowIf>
    </CommonModal>
  );
};
